import React from "react";
import { useLocalization } from "@fluent/react";
import { translateOAuthApplication } from "byzantine/src/l10n/SettingLocalizers";
import PropTypes from "prop-types";
import { Button, Dialog } from "@narmi/design_system";
import { useAppLaunch } from "../src/modules/appAuthorize";

/**
 * Default button rendering for legacy components that use
 * `children` to define the button connected to the authorize Dialog
 */
export const defaultRenderButton = (
  handleClick,
  children,
  className,
  dataClassnames,
  tabIndex,
  role
) => (
  <button
    type="button"
    className={`button--reset topMenu-link ${className}`}
    data-classname={
      dataClassnames ? `AppAuthorize ${dataClassnames}` : "AppAuthorize"
    }
    onClick={handleClick}
    tabIndex={tabIndex}
    role={role}
  >
    {children}
  </button>
);

const AppAuthorize = ({
  appId = /** @type {any} */ (undefined),
  institution = /** @type {any} */ (undefined),
  app = /** @type {any} */ (undefined),
  children = /** @type {any} */ (undefined),
  autoLaunch = /** @type {any} */ (undefined),
  className = /** @type {any} */ (undefined),
  dataClassnames = /** @type {any} */ (undefined),
  renderButton = defaultRenderButton,
}) => {
  const { l10n } = useLocalization();
  /**
   * The Tools menu (`limit_application_menu`) and the dashboard Services card
   * (`limit_application_services_menu`) are configured independently, so an app
   * reachable from this page may be present in either list -- check both, matching
   * the `can_access_naf_app` gate the backend applies.
   *
   * `institution.apps` and `institution.servicesApps` already hold deserialized
   * `App` instances (see azul/main.tsx), so there is nothing left to deserialize.
   */
  const currentApp =
    app ||
    institution?.apps?.find((a) => a.client_id === appId) ||
    institution?.servicesApps?.find((a) => a.client_id === appId) ||
    null;

  const {
    handleClick,
    handleAuthorize,
    onUserDismiss,
    isLoading,
    promptAuthorize,
  } = useAppLaunch(currentApp, autoLaunch);

  // the app is gated server side, so this is defence in depth: an app that is not
  // public, or is tile-only, is absent from both lists even when launchable
  if (!currentApp || isLoading) {
    return null;
  }

  // Localize the app data - the function handles all checking internally
  const localizedApp = translateOAuthApplication(
    {
      uuid: currentApp.id,
      name: currentApp.name,
      description: currentApp.description,
    },
    l10n
  );

  return (
    <>
      {renderButton(handleClick, children, className, dataClassnames)}
      <Dialog
        isOpen={promptAuthorize}
        onUserDismiss={onUserDismiss}
        title={l10n.getString("dialog-title-app-permission", {
          appName: localizedApp.name,
        })}
        footer={
          <>
            <Button
              kind="negative"
              onClick={onUserDismiss}
              style={{ paddingRight: "16px" }}
              label={l10n.getString("button-cancel")}
            />
            <Button
              onClick={handleAuthorize}
              label={l10n.getString("button-proceed")}
            />
          </>
        }
      >
        {l10n.getString("app-permission-disclosure", {
          appName: localizedApp.name,
        })}
        <ul>
          {Object.values(currentApp.scopes || {})
            .filter((x) => x)
            .map((scope) => (
              <li key={scope}>{scope}</li>
            ))}
        </ul>
      </Dialog>
    </>
  );
};

AppAuthorize.propTypes = {
  appId: PropTypes.string,
  institution: PropTypes.object,
  app: PropTypes.object,
  autoLaunch: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  dataClassnames: PropTypes.string,
  /**
   * Render prop called with click handler
   * renderButton: (handleClick: (e: HTMLDomEvent) => void) => void
   */
  renderButton: PropTypes.func,
};

export default AppAuthorize;
